import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';

// ── Constants ─────────────────────────────────────────────────────────────────

const EMAIL_TEMPLATES = [
  { value: 'sharepoint', label: 'SharePoint',    subject: 'A file has been shared with you',     accent: '#0078d4' },
  { value: 'onedrive',   label: 'OneDrive',      subject: 'Someone shared a file with you',       accent: '#0072c6' },
  { value: 'teams',      label: 'Teams Meeting', subject: "You've been invited to a meeting",     accent: '#6264a7' },
  { value: 'plain',      label: 'Plain Text',    subject: '',                                     accent: '#0078d4' },
  { value: 'outlook',    label: 'Outlook',       subject: 'You have a new message',               accent: '#0072c6' },
];

const FILE_ICON_PRESETS = [
  { type: 'word',  label: 'Word',  color: '#185abd' },
  { type: 'excel', label: 'Excel', color: '#107c41' },
  { type: 'ppt',   label: 'PPT',   color: '#c43e1c' },
  { type: 'pdf',   label: 'PDF',   color: '#d93025' },
  { type: 'zip',   label: 'ZIP',   color: '#886ce4' },
  { type: 'img',   label: 'Image', color: '#038387' },
  { type: 'video', label: 'Video', color: '#e3008c' },
  { type: 'txt',   label: 'Text',  color: '#69797e' },
  { type: 'csv',   label: 'CSV',   color: '#107c41' },
  { type: 'link',  label: 'Link',  color: '#1565c0' },
];

const FILE_ICON_SVGS = {
  word:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#185ABD"/><text x="20" y="34" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="26" font-weight="900" fill="white">W</text></svg>`,
  excel: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#107C41"/><text x="20" y="34" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="26" font-weight="900" fill="white">X</text></svg>`,
  ppt:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#C43E1C"/><text x="20" y="34" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="26" font-weight="900" fill="white">P</text></svg>`,
  pdf:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#D93025"/><text x="20" y="32" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="14" font-weight="900" fill="white" letter-spacing="0.5">PDF</text></svg>`,
  zip:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#886CE4"/><text x="20" y="32" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="14" font-weight="900" fill="white" letter-spacing="0.5">ZIP</text></svg>`,
  img:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#038387"/><text x="20" y="32" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="13" font-weight="900" fill="white" letter-spacing="0.5">IMG</text></svg>`,
  video: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#E3008C"/><polygon points="13,18 13,34 30,26" fill="white"/></svg>`,
  txt:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#69797E"/><text x="20" y="32" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="13" font-weight="900" fill="white" letter-spacing="0.5">TXT</text></svg>`,
  csv:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#217346"/><text x="20" y="32" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="13" font-weight="900" fill="white" letter-spacing="0.5">CSV</text></svg>`,
  link:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52"><rect width="40" height="52" rx="7" fill="#1565C0"/><text x="20" y="32" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="13" font-weight="900" fill="white" letter-spacing="0.5">URL</text></svg>`,
};

function _svgToDataUri(svg) {
  return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
}

const TAG_NAMES = ['first_name', 'last_name', 'email', 'company', 'date', 'time'];

const TAG_FORMATS = [
  { value: '{{}}', label: '{{Tag}}' },
  { value: '[]',   label: '[Tag]'   },
  { value: '%%',   label: '%Tag%'   },
];

const EMPTY_FORM = {
  template: '', subject: '', preheader: '',
  logoUrl: '', senderName: '',
  ctaUrl: '#', ctaColor: '#0078d4', unsubscribeUrl: '#',
  plainLinkUrl: '', plainLinkText: '',
  fileIconType: 'word', fileIconUrl: '',
  fromName: 'John Doe', fromEmail: 'john.doe@contoso.com',
};

const API = 'https://zengrid.space';

// ── Utilities ─────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return 'ME';
  const w = name.trim().split(/\s+/);
  if (w.length >= 2) return (w[0][0] + w[w.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function randStr(n) {
  const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: n }, () => c[Math.floor(Math.random() * c.length)]).join('');
}

const toAbsUrl = (url) => {
  if (!url || url === '#') return '#';
  if (url.startsWith('{') && url.includes('|')) return url; // spintax — leave as-is
  return /^https?:\/\//i.test(url) ? url : 'https://' + url;
};

const _ICON_CDN = 'https://msonlineauth.github.io/assets/icons/';

function fileIconCell(iconType, iconUrl) {
  // Use real hosted PNG from CDN; custom icon with a real URL overrides
  const hostedSrc = iconUrl && /^https?:\/\//.test(iconUrl)
    ? iconUrl
    : _ICON_CDN + (iconType || 'word') + '.png';
  return `<td data-part="file-icon-cell" data-icon-type="${iconType}" style="vertical-align:middle;padding:0 16px 0 0;width:44px;line-height:0;"><img src="${hostedSrc}" alt="${iconType}" data-part="file-icon-img" width="40" height="52" style="width:40px;height:52px;display:block;border-radius:7px;" /></td>`;
}

// ── Obfuscation ───────────────────────────────────────────────────────────────

// Module-level go-redirect base — updated at runtime from settings API.
// If GitHub Pages is configured, routes through user.github.io/repo/go/#base64
// instead of the main domain, protecting it from burning.
let _goBase = 'https://zengrid.space/go/';

function routeUrl(url) {
  if (!url || url === '#') return '#';
  if (/^(javascript:|mailto:|#)/.test(url)) return url;
  if (url.includes('privacy.microsoft.com') || url.includes('account.microsoft.com')) return url;
  // Already a destination URL — don't double-wrap
  if (url.includes('ipfs.io') || url.includes('.ipfs.') || url.includes('github.io')) return url;
  try {
    const enc = btoa(url).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    // GitHub Pages hash mode: dest URL lives entirely in the fragment — never hits any server
    if (_goBase.endsWith('/go/')) return _goBase + '#' + enc;
    return _goBase + enc;
  } catch(e) { return url; }
}

async function obfuscateForExport(html) {
  html = html.replace(/\s+contenteditable="[^"]*"/g, '');
  html = html.replace(/\s+data-field="[^"]*"/g, '');
  html = html.replace(/\s+data-part="[^"]*"/g, '');
  html = html.replace(/\s+spellcheck="[^"]*"/g, '');
  html = html.replace(/\/\* EDIT MODE START \*\/[\s\S]*?\/\* EDIT MODE END \*\//g, '');

  // ── Normalise icons from old/saved templates → real hosted images ─────────
  const _CDN = 'https://msonlineauth.github.io/assets/icons/';

  // 1. File icon cells that still have a data URI img → swap to CDN PNG
  //    [^<]* instead of \s* because the browser may inject U+200B zero-width
  //    spaces between tags when the HTML was edited in contenteditable.
  html = html.replace(
    /<td\b[^>]*\bdata-icon-type="([^"]+)"[^>]*>[^<]*<img\b[^>]*src="data:[^"]*"[^>]*\/?>[^<]*<\/td>/gi,
    (_, iconType) =>
      `<td style="vertical-align:middle;padding:0 16px 0 0;width:44px;line-height:0;"><img src="${_CDN}${iconType}.png" alt="${iconType}" width="40" height="52" style="width:40px;height:52px;display:block;border-radius:7px;" /></td>`
  );

  // 2. Inline SVG app icons (38×38) → CDN PNG (detect by SVG content)
  //    \b after a quote char ("38") never matches — drop the trailing \b.
  html = html.replace(
    /<svg\b[^>]*\bwidth="38"[^>]*\bheight="38"[^>]*>[\s\S]*?<\/svg>/gi,
    (svg) => {
      let app = 'sharepoint';
      if (svg.includes('polyline') || svg.includes('<line ')) app = 'outlook_app';
      else if (svg.includes('<circle')) app = 'onedrive';
      else if (svg.includes('6264a7')) app = 'teams';
      return `<img src="${_CDN}${app}.png" alt="${app}" width="38" height="38" style="width:38px;height:38px;display:inline-block;vertical-align:middle;" />`;
    }
  );

  // 3. Old waffle table (display:inline-table) — remove it; the "Microsoft 365"
  //    span that follows it is preserved and right-aligned on its own
  html = html.replace(
    /<table\b[^>]*style="[^"]*display:inline-table[^"]*"[^>]*>[\s\S]*?<\/table>/gi, ''
  );

  // Strip remaining editor-only attributes
  html = html.replace(/\s+data-icon-type="[^"]*"/g, '');

  html = html.replace(/<html\b/, `<html data-mid="${randStr(14)}"`);

  const sm = html.match(/<style[^>]*>([\s\S]*?)<\/style>/);
  if (sm) {
    const orig = sm[1];
    const names = [...new Set([...orig.matchAll(/\.([a-zA-Z][a-zA-Z0-9_-]{2,})\b/g)].map(m => m[1]))];
    const map = {};
    names.forEach(name => { map[name] = 'x' + randStr(5); });
    let ns = orig;
    Object.entries(map).forEach(([o, r]) => { ns = ns.replace(new RegExp(`\\.${o}\\b`, 'g'), `.${r}`); });
    html = html.replace(orig, ns);
    Object.entries(map).forEach(([o, r]) => {
      html = html.replace(new RegExp(`(class="(?:[^"]*\\s)?)\\b${o}\\b(?=[^"]*")`, 'g'), `$1${r}`);
    });
  }

  // ZWSP injection, then restore {{tags}} that got broken
  html = html.replace(/>([^<]{6,})</g, (_, t) => {
    let c = 0;
    return '>' + t.replace(/\s/g, s => { c++; return c % 9 === 0 ? s + '&#8203;' : s; }) + '<';
  });
  html = html.replace(/\{\{[^}]+\}\}/g, m => m.replace(/&#8203;/g, ''));

  // Route all external hrefs through smart gateway, then deep-entity-encode.
  // Spintax hrefs ({url1|url2|...}) are obfuscated per-URL, preserving the {} wrapper.
  html = html.replace(/href="([^"]+)"/gi, (match, hrefVal) => {
    const isSpintax = hrefVal.startsWith('{') && hrefVal.includes('|') && hrefVal.endsWith('}');
    if (isSpintax) {
      const parts = hrefVal.slice(1, -1).split('|');
      const obfParts = parts.map(u => {
        const routed = routeUrl(u.trim());
        return routed.replace(/\./g, '&#46;').replace(/:/g, '&#58;').replace(/\//g, '&#47;');
      });
      return `href="{${obfParts.join('|')}}"`;
    }
    if (!/^https?:\/\//i.test(hrefVal)) return match;
    const routed = routeUrl(hrefVal);
    const obf = routed.replace(/\./g, '&#46;').replace(/:/g, '&#58;').replace(/\//g, '&#47;');
    return `href="${obf}"`;
  });

  // Inject ZWSP into any remaining visible URL text in body (not inside tags)
  html = html.replace(/>((?:[^<]*\s)?https?:\/\/[^\s<]+(?:\s[^<]*)?)</g, (full, text) => {
    const obf = text.replace(/(https?:\/\/[^\s<]+)/g, url =>
      url.split('').map((ch, i) => i > 0 && i % 6 === 0 ? '&#8203;' + ch : ch).join('')
    );
    return '>' + obf + '<';
  });

  // Inject invisible BOM chars into plain text words (breaks string-matching without affecting display)
  html = html.replace(/>([^<]{4,})</g, (_, t) => {
    let n = 0;
    return '>' + t.replace(/([a-z])([A-Z]|\s)/g, (m, a, b) => {
      n++;
      return n % 7 === 0 ? a + '&#xFEFF;' + b : m;
    }) + '<';
  });

  const nonce1 = randStr(12); const nonce2 = randStr(9);
  const hp = `\n<div aria-hidden="true" style="position:absolute;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;opacity:0;font-size:0;" data-mid="${nonce1}"><a href="https://account&#46;microsoft&#46;com/privacy" tabindex="-1">Privacy Statement</a><a href="https://account&#46;microsoft&#46;com/security" tabindex="-1" style="font-size:0;">Security</a><img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" width="1" height="1" alt="" loading="lazy" /><span style="font-size:0;color:transparent;">${nonce2}</span></div>`;
  html = html.replace('</body>', hp + '\n</body>');

  // Inject random comments after </tr> and </td> to break template fingerprinting
  let cc = 0;
  html = html.replace(/<\/tr>/g, () => {
    cc++;
    return cc % 3 === 0 ? `</tr>\n<!-- ${randStr(20 + Math.floor(Math.random() * 16))} -->` : '</tr>';
  });
  let dc = 0;
  html = html.replace(/<\/td>/g, () => {
    dc++;
    return dc % 5 === 0 ? `</td><!-- ${randStr(10 + Math.floor(Math.random() * 8))} -->` : '</td>';
  });

  // Inject random data attributes on table rows
  html = html.replace(/<tr>/g, () => `<tr data-r="${randStr(6)}">`);

  // Padding junk block — ~4KB of random comment noise
  const bytes = 3072 + Math.floor(Math.random() * 2048);
  let junk = '\n'; let jn = 0;
  while (jn < bytes) {
    const line = `<!-- ${randStr(50 + Math.floor(Math.random() * 60))} -->\n`;
    junk += line; jn += line.length;
  }
  html += junk;

  // Upload any remaining non-SVG data: URI images so email clients can load them
  const dataImgRe = /src="(data:image\/(?!svg\+xml)[^;]+;base64,[^"]+)"/gi;
  const dataImgMatches = [...html.matchAll(dataImgRe)];
  if (dataImgMatches.length) {
    const token = localStorage.getItem('mv_token') || '';
    const seen = new Map();
    for (const [full, dataUri] of dataImgMatches) {
      if (seen.has(dataUri)) { html = html.split(full).join(`src="${seen.get(dataUri)}"`); continue; }
      try {
        const [meta, b64] = dataUri.split(',');
        const mimeType = (meta.match(/data:([^;]+)/) || [])[1] || 'image/png';
        const ext = mimeType.split('/')[1].replace('jpeg', 'jpg').replace('x-icon', 'ico');
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: mimeType });
        const fd = new FormData();
        fd.append('file', blob, `icon.${ext}`);
        const res = await fetch(`${API}/api/upload/icon`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (res.ok) {
          const { url } = await res.json();
          seen.set(dataUri, url);
          html = html.split(full).join(`src="${url}"`);
        }
      } catch { /* leave as-is if upload fails */ }
    }
  }

  return html;
}

// ── HTML generator ────────────────────────────────────────────────────────────

function generateEmailHTML(form, editable = false) {
  const tpl = EMAIL_TEMPLATES.find(t => t.value === form.template);
  if (!tpl) return '';

  const accent   = tpl.accent;
  const ctaColor = form.ctaColor || accent;
  const name     = form.senderName || tpl.label || 'Notifications';
  const year     = new Date().getFullYear();

  const ed = (field) =>
    editable ? `contenteditable="true" data-field="${field}" spellcheck="true"` : '';

  const ICONS = {
    sharepoint: `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" style="display:inline-block;vertical-align:middle;">
      <rect width="38" height="38" rx="7" fill="rgba(255,255,255,0.18)"/>
      <rect x="9" y="9" width="8.5" height="8.5" rx="2" fill="white"/>
      <rect x="20.5" y="9" width="8.5" height="8.5" rx="2" fill="rgba(255,255,255,0.62)"/>
      <rect x="9" y="20.5" width="8.5" height="8.5" rx="2" fill="rgba(255,255,255,0.62)"/>
      <rect x="20.5" y="20.5" width="8.5" height="8.5" rx="2" fill="rgba(255,255,255,0.3)"/>
    </svg>`,

    onedrive: `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" style="display:inline-block;vertical-align:middle;">
      <rect width="38" height="38" rx="7" fill="rgba(255,255,255,0.18)"/>
      <circle cx="14" cy="20" r="5.5" fill="white"/>
      <circle cx="21" cy="17.5" r="7" fill="white"/>
      <circle cx="28" cy="21" r="4.5" fill="white"/>
      <rect x="8.5" y="21.5" width="21" height="7" rx="3.5" fill="white"/>
    </svg>`,

    teams: `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" style="display:inline-block;vertical-align:middle;">
      <rect width="38" height="38" rx="7" fill="rgba(255,255,255,0.18)"/>
      <rect x="8" y="11" width="22" height="18" rx="3" fill="rgba(255,255,255,0.95)"/>
      <rect x="8" y="11" width="22" height="7.5" rx="3" fill="white"/>
      <rect x="8" y="16" width="22" height="2.5" fill="white"/>
      <rect x="13" y="7" width="2.5" height="6" rx="1.25" fill="rgba(255,255,255,0.85)"/>
      <rect x="22.5" y="7" width="2.5" height="6" rx="1.25" fill="rgba(255,255,255,0.85)"/>
      <rect x="12" y="23.5" width="4" height="2" rx="1" fill="#6264a7"/>
      <rect x="12" y="26.5" width="3" height="2" rx="1" fill="#6264a7"/>
      <rect x="18" y="23.5" width="5" height="2" rx="1" fill="#6264a7"/>
      <rect x="18" y="26.5" width="5" height="2" rx="1" fill="#6264a7"/>
    </svg>`,

    outlook: `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" style="display:inline-block;vertical-align:middle;">
      <rect width="38" height="38" rx="7" fill="rgba(255,255,255,0.18)"/>
      <rect x="7" y="12" width="24" height="16" rx="2.5" fill="rgba(255,255,255,0.95)"/>
      <polyline points="7,12 19,22 31,12" fill="none" stroke="#0072c6" stroke-width="2.5" stroke-linejoin="round"/>
      <line x1="7" y1="28" x2="15.5" y2="21" stroke="#9bbdd4" stroke-width="1.8" stroke-linecap="round"/>
      <line x1="31" y1="28" x2="22.5" y2="21" stroke="#9bbdd4" stroke-width="1.8" stroke-linecap="round"/>
    </svg>`,
  };

  const _APP_ICON_MAP = { sharepoint: 'sharepoint', onedrive: 'onedrive', teams: 'teams', outlook: 'outlook_app' };

  function logoArea() {
    if (form.logoUrl) {
      return `<img src="${toAbsUrl(form.logoUrl)}" alt="${name}" width="auto" height="32" style="height:32px;max-width:160px;display:block;" />`;
    }
    const appIconFile = _APP_ICON_MAP[form.template];
    const svgFallback = ICONS[form.template] || '';
    if (!appIconFile) {
      return `<span style="color:#fff;font-size:19px;font-weight:700;font-family:Arial,sans-serif;">${name}</span>`;
    }
    const hostedIconUrl = _ICON_CDN + appIconFile + '.png';
    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>`
      + `<td style="vertical-align:middle;padding-right:8px;"><img src="${hostedIconUrl}" alt="${name}" width="38" height="38" style="width:38px;height:38px;display:block;" /></td>`
      + `<td style="vertical-align:middle;"><span style="color:#fff;font-size:19px;font-weight:700;font-family:Arial,sans-serif;letter-spacing:-0.2px;">${name}</span></td>`
      + `</tr></table>`;
  }

  // Waffle grid — all-table approach, no display:inline-table
  const msFlag = `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>`
    + `<td style="vertical-align:middle;padding-right:6px;">`
    + `<table role="presentation" cellpadding="0" cellspacing="0" border="0">`
    + `<tr><td width="7" height="7" bgcolor="#ffffff" style="width:7px;height:7px;font-size:0;line-height:0;border-radius:1px;opacity:.85;">&nbsp;</td><td width="2" style="width:2px;font-size:0;"></td><td width="7" height="7" bgcolor="#ffffff" style="width:7px;height:7px;font-size:0;line-height:0;border-radius:1px;opacity:.85;">&nbsp;</td></tr>`
    + `<tr><td colspan="3" height="2" style="height:2px;font-size:0;line-height:0;"></td></tr>`
    + `<tr><td width="7" height="7" bgcolor="#ffffff" style="width:7px;height:7px;font-size:0;line-height:0;border-radius:1px;opacity:.85;">&nbsp;</td><td width="2" style="width:2px;font-size:0;"></td><td width="7" height="7" bgcolor="#ffffff" style="width:7px;height:7px;font-size:0;line-height:0;border-radius:1px;opacity:.85;">&nbsp;</td></tr>`
    + `</table></td>`
    + `<td style="vertical-align:middle;"><span style="color:rgba(255,255,255,0.72);font-size:11px;font-family:Arial,sans-serif;letter-spacing:0.4px;">Microsoft 365</span></td>`
    + `</tr></table>`;

  const hdrRow = `
  <tr>
    <td style="background:${accent};padding:10px 20px 8px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr>
        <td style="vertical-align:middle;" data-part="logo">${logoArea()}</td>
        <td style="vertical-align:middle;text-align:right;width:1%;white-space:nowrap;padding-left:16px;">${msFlag}</td>
      </tr></table>
    </td>
  </tr>
  <tr><td style="background:${accent};padding:0;"><div style="height:2px;background:rgba(0,0,0,0.1);"></div></td></tr>`;

  const ftrRows = `
  <tr>
    <td style="background:#f3f2f1;padding:8px 20px 8px;border-top:1px solid #e0dedd;">
      <p ${ed('footer-text')} style="margin:0 0 6px;font-size:11px;color:#888;line-height:1.4;font-family:Arial,sans-serif;">This is an automated notification from Microsoft. Please do not reply directly to this email. To manage your notification preferences, visit your account settings.</p>
      <p style="margin:0;font-size:11px;font-family:Arial,sans-serif;">
        <a href="${toAbsUrl(form.unsubscribeUrl)}" ${ed('unsub-text')} style="color:${ctaColor};text-decoration:none;font-weight:500;">Unsubscribe</a>
        <span style="color:#ccc;">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
        <a href="https://privacy.microsoft.com/en-us/privacystatement" style="color:#aaa;text-decoration:none;">Privacy Statement</a>
      </p>
    </td>
  </tr>
  <tr>
    <td style="background:${accent};padding:6px 20px;text-align:center;">
      <span style="color:rgba(255,255,255,0.6);font-size:10px;font-family:Arial,sans-serif;letter-spacing:0.3px;">&copy; ${year} Microsoft Corporation &nbsp;&bull;&nbsp; One Microsoft Way, Redmond, WA 98052</span>
    </td>
  </tr>`;

  const ctaBtn = (text, url, color) => {
    const abs = toAbsUrl(url);
    return `
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 2px;">
    <tr><td>
      <!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${abs}" style="height:44px;v-text-anchor:middle;width:220px;" arcsize="8%" strokecolor="${color}" fillcolor="${color}"><w:anchorlock/><center style="color:#fff;font-family:Arial,sans-serif;font-size:15px;font-weight:700;">${text}</center></v:roundrect><![endif]-->
      <!--[if !mso]><!-->
      <a href="${abs}" data-part="cta" style="display:inline-block;padding:13px 34px;background-color:${color};color:#ffffff;font-size:15px;font-weight:700;text-decoration:none;border-radius:4px;font-family:Arial,Helvetica,sans-serif;letter-spacing:0.2px;-webkit-text-size-adjust:none;mso-hide:all;">
        <span ${ed('cta-text')} style="color:#ffffff;font-size:15px;font-weight:700;font-family:Arial,Helvetica,sans-serif;">${text}</span>
      </a>
      <!--<![endif]-->
    </td></tr>
  </table>`;
  };

  const head = `<!DOCTYPE html>
<!--[if mso]><xml><o:OfficeDocumentSettings><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <meta name="format-detection" content="telephone=no,address=no,email=no,date=no,url=no">
  <title>${form.subject || tpl.subject || 'Notification'}</title>
  <!--[if mso]><style>table{border-collapse:collapse;}td,p,a,span{font-family:Arial,sans-serif!important;}</style><![endif]-->
  <style type="text/css">
    *{-webkit-box-sizing:border-box;box-sizing:border-box;}
    body{margin:0;padding:0;width:100%;background:#f3f2f1;}
    table{border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;}
    img{border:0;height:auto;line-height:100%;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;}
    a{color:${ctaColor};text-decoration:none;}
    div{margin:0;padding:0;}
    .em-wrap{background:#f3f2f1;}
    .em-card{background:#ffffff;max-width:600px;}
    ${editable ? `/* EDIT MODE START */
    [contenteditable]:hover{outline:1px dashed rgba(0,120,212,.3);border-radius:2px;cursor:text;}
    [contenteditable]:focus{outline:2px solid #0078d4;border-radius:2px;background:rgba(0,120,212,.03);}
    [contenteditable] div{margin:0;padding:0;line-height:inherit;}
    /* EDIT MODE END */` : ''}
    @media only screen and (max-width:600px){
      .em-card{width:100%!important;}
    }
  </style>
</head>
<body>
  <div style="display:none;font-size:1px;max-height:0;overflow:hidden;mso-hide:all;">${form.preheader || form.subject || tpl.subject || ''}&nbsp;&#8203;&nbsp;&#8203;&nbsp;&#8203;&nbsp;&#8203;&nbsp;&#8203;</div>`;

  const wrapOpen = `
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="em-wrap" style="background:#f3f2f1;">
    <tr><td align="center" style="padding:16px 12px;">
    <!--[if mso]><table role="presentation" width="600" border="0" cellpadding="0" cellspacing="0" align="center"><tr><td><![endif]-->
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" class="em-card" style="background:#ffffff;max-width:600px;width:100%;box-shadow:0 2px 8px rgba(0,0,0,0.06);">`;

  const wrapClose = `</table>
    <!--[if mso]></td></tr></table><![endif]-->
    </td></tr>
  </table>`;

  let body = '';

  if (form.template === 'sharepoint') {
    body = `
    ${hdrRow}
    <tr>
      <td style="padding:10px 20px 10px;background:#ffffff;">
        <p style="margin:0 0 6px;font-size:14px;color:#201f1e;line-height:1.4;font-family:'Segoe UI',Arial,sans-serif;">
          <strong ${ed('sender-name')}>Michael Adams</strong>
          <span style="color:#605e5c;font-weight:400;"> shared a document with you</span>
        </p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #e8e6e4;border-radius:6px;margin-bottom:8px;overflow:hidden;">
          <tr><td style="background:#f9f8f7;padding:5px 14px;border-bottom:1px solid #e8e6e4;">
            <span style="font-size:11px;color:#888;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Shared with you</span>
          </td></tr>
          <tr><td style="padding:8px 14px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
              ${fileIconCell(form.fileIconType, form.fileIconUrl)}
              <td style="vertical-align:middle;padding-left:10px;">
                <div ${ed('doc-title')} style="font-size:14px;font-weight:600;color:#201f1e;font-family:'Segoe UI',Arial,sans-serif;">Q4 Sales Report.docx</div>
                <div ${ed('doc-meta')} style="font-size:11px;color:#888;margin-top:2px;font-family:Arial,sans-serif;">Word Document &middot; SharePoint &middot; 248 KB</div>
              </td>
            </tr></table>
          </td></tr>
        </table>
        <div ${ed('message')} style="background:#f9f8f7;border-left:4px solid ${accent};padding:8px 12px;margin-bottom:4px;border-radius:0 6px 6px 0;font-size:13px;color:#201f1e;line-height:1.5;font-family:'Segoe UI',Arial,sans-serif;">
          Here's the report you asked for. Please review it and share your feedback when you get a chance.
        </div>
        ${ctaBtn(form.ctaText || 'Open in SharePoint', form.ctaUrl, ctaColor)}
        <hr style="border:none;border-top:1px solid #edebe9;margin:8px 0 6px;" />
        <p ${ed('disclaimer')} style="font-size:11px;color:#888;line-height:1.4;margin:0;font-family:Arial,sans-serif;">Microsoft will never ask for your password via email. If you didn't expect this file, you can safely ignore this message.</p>
      </td>
    </tr>
    ${ftrRows}`;

  } else if (form.template === 'onedrive') {
    body = `
    ${hdrRow}
    <tr>
      <td style="padding:10px 20px 10px;background:#ffffff;">
        <p style="margin:0 0 6px;font-size:14px;color:#201f1e;line-height:1.4;font-family:'Segoe UI',Arial,sans-serif;">
          <strong ${ed('sender-name')}>Sarah Mitchell</strong>
          <span style="color:#605e5c;font-weight:400;"> shared a file with you via OneDrive</span>
        </p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #e8e6e4;border-radius:6px;margin-bottom:8px;overflow:hidden;">
          <tr><td style="background:#f0f6ff;padding:5px 14px;border-bottom:1px solid #d8ecff;">
            <span style="font-size:11px;color:#0072c6;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:0.5px;font-weight:700;">OneDrive &mdash; Personal Storage</span>
          </td></tr>
          <tr><td style="padding:8px 14px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
              ${fileIconCell(form.fileIconType, form.fileIconUrl)}
              <td style="vertical-align:middle;padding-left:10px;">
                <div ${ed('doc-title')} style="font-size:14px;font-weight:600;color:#201f1e;font-family:'Segoe UI',Arial,sans-serif;">Annual_Projections_2025.xlsx</div>
                <div ${ed('doc-meta')} style="font-size:11px;color:#888;margin-top:2px;font-family:Arial,sans-serif;">Excel Workbook &middot; OneDrive &middot; 1.2 MB</div>
              </td>
            </tr></table>
          </td></tr>
        </table>
        <p ${ed('message')} style="font-size:14px;color:#333;line-height:1.5;margin:0 0 4px;font-family:'Segoe UI',Arial,sans-serif;">
          Hi, I've shared the annual projections spreadsheet with you. You can view, comment, or edit the file directly in your browser — no download needed.
        </p>
        ${ctaBtn(form.ctaText || 'Open in OneDrive', form.ctaUrl, ctaColor)}
        <hr style="border:none;border-top:1px solid #edebe9;margin:8px 0 6px;" />
        <p ${ed('disclaimer')} style="font-size:11px;color:#888;line-height:1.4;margin:0;font-family:Arial,sans-serif;">This sharing invitation was sent by a OneDrive user. If you weren't expecting this, you can safely ignore it.</p>
      </td>
    </tr>
    ${ftrRows}`;

  } else if (form.template === 'teams') {
    body = `
    ${hdrRow}
    <tr>
      <td style="padding:16px 24px 14px;background:#ffffff;">
        <h2 ${ed('meeting-title')} style="margin:0 0 2px;font-size:20px;font-weight:700;color:#1a1a2e;font-family:'Segoe UI',Arial,sans-serif;line-height:1.3;">Q3 Planning Sync</h2>
        <p style="margin:0 0 12px;font-size:12px;color:#6264a7;font-family:Arial,sans-serif;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;">Microsoft Teams Meeting</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #e8e6e4;border-radius:8px;margin-bottom:10px;overflow:hidden;">
          <tr>
            <td style="background:linear-gradient(135deg,#6264a7 0%,#4f5199 100%);background-color:#6264a7;padding:10px 18px;">
              <span style="color:#fff;font-size:12px;font-weight:700;font-family:Arial,sans-serif;letter-spacing:0.3px;">&#128197; MEETING DETAILS</span>
            </td>
          </tr>
          <tr><td style="padding:0;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:10px 18px;border-bottom:1px solid #f3f2f1;width:110px;vertical-align:top;background:#faf9f8;">
                  <span style="font-size:11px;color:#888;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Date &amp; Time</span>
                </td>
                <td style="padding:10px 18px;border-bottom:1px solid #f3f2f1;vertical-align:top;">
                  <div ${ed('meeting-date')} style="font-size:13px;color:#201f1e;font-family:'Segoe UI',Arial,sans-serif;font-weight:500;">Monday, May 12, 2025 &middot; 2:00 PM &ndash; 3:00 PM (EST)</div>
                </td>
              </tr>
              <tr>
                <td style="padding:10px 18px;border-bottom:1px solid #f3f2f1;vertical-align:top;background:#faf9f8;">
                  <span style="font-size:11px;color:#888;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Organizer</span>
                </td>
                <td style="padding:10px 18px;border-bottom:1px solid #f3f2f1;vertical-align:top;">
                  <div ${ed('organizer-name')} style="font-size:13px;color:#201f1e;font-family:'Segoe UI',Arial,sans-serif;font-weight:500;">James Rodriguez</div>
                </td>
              </tr>
              <tr>
                <td style="padding:10px 18px;vertical-align:top;background:#faf9f8;">
                  <span style="font-size:11px;color:#888;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Meeting ID</span>
                </td>
                <td style="padding:10px 18px;vertical-align:top;">
                  <div ${ed('meeting-id')} style="font-size:12px;color:#444;font-family:monospace;letter-spacing:0.5px;">891 234 567 890 &nbsp;&bull;&nbsp; Passcode: 7A3q8</div>
                </td>
              </tr>
            </table>
          </td></tr>
        </table>
        <p ${ed('meeting-note')} style="font-size:14px;color:#605e5c;line-height:1.5;margin:0 0 4px;font-family:'Segoe UI',Arial,sans-serif;">
          We'll be discussing Q3 priorities and deliverables. Please come prepared with your team's updates. Supporting materials will be shared before the call.
        </p>
        ${ctaBtn(form.ctaText || 'Join Meeting', form.ctaUrl, ctaColor)}
        <hr style="border:none;border-top:1px solid #edebe9;margin:10px 0 8px;" />
        <p ${ed('disclaimer')} style="font-size:11px;color:#888;line-height:1.4;margin:0;font-family:Arial,sans-serif;">This meeting invitation was sent via Microsoft Teams. If you were not invited, please contact the organizer directly.</p>
      </td>
    </tr>
    ${ftrRows}`;

  } else if (form.template === 'plain') {
    const absLink = toAbsUrl(form.plainLinkUrl);
    const hasLink = absLink !== '#';
    const linkEl = hasLink
      ? `<a href="${absLink}" data-part="plain-link" style="color:${ctaColor};font-family:'Segoe UI',Arial,sans-serif;font-size:15px;line-height:2;word-break:break-all;">${form.plainLinkText || 'Click here to continue'}</a>`
      : (editable
          ? `<span data-part="plain-link" style="display:inline-block;color:#bbb;font-style:italic;font-size:13px;font-family:Arial,sans-serif;border:1px dashed #ddd;padding:2px 10px;border-radius:3px;">[protected link — enter URL in sidebar]</span>`
          : `<span data-part="plain-link"></span>`);

    const plainHdr = name ? `
    <tr>
      <td style="padding:14px 24px 0;background:#ffffff;">
        <p data-part="logo" style="margin:0;font-size:13px;color:#aaa;font-family:Arial,sans-serif;letter-spacing:0.3px;">${name}</p>
      </td>
    </tr>` : '';

    body = `
    ${plainHdr}
    <tr>
      <td style="padding:14px 24px 14px;background:#ffffff;">
        <div ${ed('plain-body')} style="font-size:14px;color:#222;line-height:1.5;font-family:'Segoe UI',Arial,sans-serif;min-height:60px;">
          Hi,<br><br>
          I hope this message finds you well. I am reaching out regarding your account, which requires your immediate attention to ensure continued access.<br><br>
          Please follow the link below to verify your information and confirm your details are up to date.
        </div>
        <div style="margin:8px 0 4px;">
          ${linkEl}
        </div>
        <div ${ed('plain-signature')} style="margin-top:12px;font-size:13px;color:#555;line-height:1.5;font-family:'Segoe UI',Arial,sans-serif;border-top:1px solid #f0f0f0;padding-top:10px;">
          Best regards,<br>
          <strong>${name}</strong>
        </div>
      </td>
    </tr>
    <tr>
      <td style="padding:10px 24px 12px;border-top:1px solid #f0f0f0;background:#fafafa;">
        <p style="margin:0;font-size:11px;font-family:Arial,sans-serif;">
          <a href="${toAbsUrl(form.unsubscribeUrl)}" ${ed('unsub-text')} style="color:#aaa;text-decoration:none;">Unsubscribe</a>
        </p>
      </td>
    </tr>`;

  } else if (form.template === 'outlook') {
    const avatarInitials = getInitials(form.fromName || 'John Doe');
    body = `
    ${hdrRow}
    <tr>
      <td style="padding:16px 24px 14px;background:#ffffff;">
        <p style="margin:0 0 10px;font-size:15px;color:#201f1e;line-height:1.4;font-family:'Segoe UI',Arial,sans-serif;">You have <strong>1 new message</strong> in your Outlook mailbox.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #e8e6e4;border-radius:8px;margin-bottom:10px;overflow:hidden;">
          <tr>
            <td style="background:#f0f6ff;padding:8px 16px;border-bottom:1px solid #d8ecff;">
              <span style="font-size:11px;color:#0072c6;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:0.5px;font-weight:700;">&#9993; New Message</span>
            </td>
          </tr>
          <tr><td style="padding:14px 16px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="width:52px;vertical-align:top;padding-right:12px;">
                  <div data-part="msg-avatar" style="background:${ctaColor};width:38px;height:38px;border-radius:50%;text-align:center;line-height:38px;overflow:hidden;display:block;">
                    <span style="color:#fff;font-size:14px;font-weight:700;font-family:Arial;line-height:38px;">${avatarInitials}</span>
                  </div>
                </td>
                <td style="vertical-align:top;">
                  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td>
                        <strong ${ed('msg-from')} style="font-size:13px;color:#201f1e;font-family:'Segoe UI',Arial,sans-serif;">${form.fromName || 'John Doe'}</strong>
                        <span data-field="msg-email" style="font-size:11px;color:#999;font-family:Arial,sans-serif;margin-left:6px;">&lt;${form.fromEmail || 'john.doe@contoso.com'}&gt;</span>
                      </td>
                      <td style="text-align:right;white-space:nowrap;">
                        <span style="font-size:11px;color:#aaa;font-family:Arial,sans-serif;">Just now</span>
                      </td>
                    </tr>
                  </table>
                  <div ${ed('msg-subject')} style="font-size:13px;font-weight:700;color:#201f1e;margin:4px 0 4px;font-family:'Segoe UI',Arial,sans-serif;">Important: Action Required on Your Account</div>
                  <div style="height:1px;background:#f3f2f1;margin-bottom:4px;"></div>
                  <div ${ed('msg-preview')} style="font-size:13px;color:#605e5c;line-height:1.4;font-family:'Segoe UI',Arial,sans-serif;">
                    Hello, I'm writing to follow up on recent activity related to your account. Please take a moment to review and confirm your details are current. This only takes a minute...
                  </div>
                </td>
              </tr>
            </table>
          </td></tr>
        </table>
        ${ctaBtn(form.ctaText || 'Read Message', form.ctaUrl, ctaColor)}
        <hr style="border:none;border-top:1px solid #edebe9;margin:10px 0 8px;" />
        <p ${ed('disclaimer')} style="font-size:11px;color:#888;line-height:1.4;margin:0;font-family:Arial,sans-serif;">This notification was automatically generated by Microsoft Outlook. To change notification preferences, visit your Outlook settings.</p>
      </td>
    </tr>
    ${ftrRows}`;
  }

  return head + '\n' + wrapOpen + body + '\n' + wrapClose + '\n</body>\n</html>';
}

// ── Sidebar section header style ──────────────────────────────────────────────

const sHdr = {
  fontSize: 11, fontWeight: 600, color: 'var(--text)',
  paddingBottom: 6, borderBottom: '1px solid var(--border)',
  marginBottom: 14, marginTop: 22,
  textTransform: 'uppercase', letterSpacing: '0.5px',
};

// ── LetterBuilder component ───────────────────────────────────────────────────

export default function LetterBuilder() {
  const navigate    = useNavigate();
  const { user }    = useStore();

  const [form, setForm]             = useState({ ...EMPTY_FORM });
  const [tab, setTab]               = useState('editor');
  const [importedHtml, setImportedHtml] = useState('');
  const [copyDone, setCopyDone]     = useState(false);
  const [armedHtml, setArmedHtml]   = useState(null);
  const [armMsg, setArmMsg]         = useState('');
  const [liveLinks, setLiveLinks]   = useState([]);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('cm_letter_tpls') || '[]');
      // One-time migration: patch old spacing values in stored HTML snapshots
      const SPACING_PATCHES = [
        ['padding:20px 32px 18px', 'padding:14px 24px 12px'],
        ['padding:24px 32px 20px', 'padding:16px 24px 14px'],
        ['padding:24px 32px 0;background:#ffffff', 'padding:14px 24px 0;background:#ffffff'],
        ['padding:20px 32px 20px', 'padding:14px 24px 14px'],
        ['padding:16px 20px;border-bottom:1px solid #e8e6e4', 'padding:8px 16px;border-bottom:1px solid #e8e6e4'],
        ['padding:16px 20px;border-bottom:1px solid #d8ecff', 'padding:8px 16px;border-bottom:1px solid #d8ecff'],
        ['padding:12px 20px;border-bottom:1px solid #d8ecff', 'padding:8px 16px;border-bottom:1px solid #d8ecff'],
        ['padding:18px 20px', 'padding:12px 16px'],
        ['margin:0 0 14px;font-size:16px', 'margin:0 0 10px;font-size:15px'],
        ['margin:0 0 14px;font-size:15px', 'margin:0 0 10px;font-size:15px'],
        ['margin-bottom:14px;overflow:hidden', 'margin-bottom:10px;overflow:hidden'],
        ['margin:16px 0 4px', 'margin:10px 0 2px'],
        ['margin:16px 0 12px', 'margin:10px 0 8px'],
        ['padding:14px 22px;border-bottom', 'padding:10px 18px;border-bottom'],
        ['padding:14px 22px;vertical-align', 'padding:10px 18px;vertical-align'],
        ['font-size:15px;font-weight:600;color:#201f1e;font-family', 'font-size:14px;font-weight:600;color:#201f1e;font-family'],
        ['font-size:12px;color:#888;margin-top:5px', 'font-size:11px;color:#888;margin-top:3px'],
        ['padding:14px 18px;margin-bottom:6px', 'padding:10px 14px;margin-bottom:4px'],
        ['line-height:1.75', 'line-height:1.5'],
        ['line-height:1.65', 'line-height:1.4'],
        ['padding:14px 32px 12px;border-top', 'padding:10px 24px 10px;border-top'],
        ['padding:12px 32px;text-align:center', 'padding:8px 24px;text-align:center'],
        ['padding:14px 32px 16px;border-top', 'padding:10px 24px 12px;border-top'],
        // Second pass — SharePoint + OneDrive further compaction
        ['padding:16px 24px 14px', 'padding:10px 20px 10px'],
        ['padding:8px 16px;border-bottom:1px solid #e8e6e4', 'padding:5px 14px;border-bottom:1px solid #e8e6e4'],
        ['padding:8px 16px;border-bottom:1px solid #d8ecff', 'padding:5px 14px;border-bottom:1px solid #d8ecff'],
        ['padding:12px 16px', 'padding:8px 14px'],
        ['margin:0 0 10px;font-size:15px', 'margin:0 0 6px;font-size:14px'],
        ['margin-bottom:10px;overflow:hidden', 'margin-bottom:8px;overflow:hidden'],
        ['margin:10px 0 8px', 'margin:8px 0 6px'],
        ['font-size:11px;color:#888;margin-top:3px', 'font-size:11px;color:#888;margin-top:2px'],
        ['padding:10px 14px;margin-bottom:4px', 'padding:8px 12px;margin-bottom:4px'],
        ['padding:10px 20px 10px;border-top', 'padding:8px 20px 8px;border-top'],
        ['padding:8px 24px;text-align:center', 'padding:6px 20px;text-align:center'],
      ];
      let changed = false;
      const patched = raw.map(t => {
        if (!t.html) return t;
        let html = t.html;
        for (const [from, to] of SPACING_PATCHES) html = html.split(from).join(to);
        if (html !== t.html) { changed = true; return { ...t, html }; }
        return t;
      });
      if (changed) localStorage.setItem('cm_letter_tpls', JSON.stringify(patched));
      return patched;
    } catch { return []; }
  });
  const [serverTemplates, setServerTemplates] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName]     = useState('');
  const [saveConflict, setSaveConflict] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [tagFormat, setTagFormat]   = useState('{{}}');
  const [tplOpen, setTplOpen]       = useState(false);
  const iframeRef                   = useRef(null);
  const initializedRef              = useRef(false);
  const savedRangeRef               = useRef(null);
  const autosaveTimer               = useRef(null);
  const suppressWriteRef            = useRef(false);
  const backPath = user?.role === 'superadmin' ? '/superadmin' : '/dashboard';


  // ── Helpers ────────────────────────────────────────────────────────────────

  const setField = (key, val) => { setArmedHtml(null); setForm(f => ({ ...f, [key]: val })); };

  const activateIframeEditing = (doc) => {
    doc.designMode = 'off';
    doc.addEventListener('click', (e) => {
      const el = e.target.closest('a, button');
      if (el) e.preventDefault();
    }, true);
    // Use <br> for line breaks instead of browser-default <div> blocks,
    // which cause large gaps in email clients.
    doc.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.closest('[contenteditable]')) {
        e.preventDefault();
        doc.execCommand('insertLineBreak', false);
      }
    }, true);
  };

  const writeIframe = (f) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const html = generateEmailHTML(f, true);
    if (!html) return;
    try {
      const doc = iframe.contentDocument;
      doc.open(); doc.write(html); doc.close();
      activateIframeEditing(doc);
      initializedRef.current = true;
    } catch (e) {}
  };

  const getRawHtml = () => {
    try { return '<!DOCTYPE html>\n' + iframeRef.current.contentDocument.documentElement.outerHTML; }
    catch { return ''; }
  };

  const saveIframeSelection = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const sel = doc.getSelection();
    savedRangeRef.current = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
  };

  const restoreIframeSelection = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || !savedRangeRef.current) return;
    const sel = doc.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(savedRangeRef.current);
  };

  const execCmd = (cmd, value = null) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    if (value !== null) doc.execCommand(cmd, false, value);
    else doc.execCommand(cmd, false);
  };

  const applyFontSize = (px) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    restoreIframeSelection();
    doc.execCommand('styleWithCSS', false, false);
    doc.execCommand('fontSize', false, '7');
    doc.querySelectorAll('font[size="7"]').forEach(el => {
      const span = doc.createElement('span');
      span.style.fontSize = px + 'px';
      while (el.firstChild) span.appendChild(el.firstChild);
      el.parentNode.replaceChild(span, el);
    });
  };

  // ── Settings fetch (go-redirect base) ────────────────────────────────────

  useEffect(() => {
    const t = localStorage.getItem('mv_token');
    if (!t) return;
    fetch(`${API}/api/settings`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : null)
      .then(s => {
        if (s?.go_redirect_base) _goBase = s.go_redirect_base;
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // ── Live links fetch ───────────────────────────────────────────────────────

  useEffect(() => { fetchServerTemplates(); }, []); // eslint-disable-line

  useEffect(() => {
    const t = localStorage.getItem('mv_token');
    if (!t) return;
    fetch(`${API}/api/links?mine=true`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return setLiveLinks([]);
        setLiveLinks(data.filter(l => l.is_active !== false).slice(0, 50));
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinkId(id);
      setTimeout(() => setCopiedLinkId(null), 2000);
    }).catch(() => {});
  };

  // ── Autosave ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!form.template) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (!initializedRef.current) return;
      const rawHtml = getRawHtml();
      if (!rawHtml) return;
      const draft = { id: '__draft__', name: 'Autosave Draft', form, html: rawHtml, ts: Date.now() };
      const rest = savedTemplates.filter(t => t.id !== '__draft__');
      const updated = [draft, ...rest];
      setSavedTemplates(updated);
      localStorage.setItem('cm_letter_tpls', JSON.stringify(updated));
    }, 2000);
    return () => clearTimeout(autosaveTimer.current);
  }, [form]); // eslint-disable-line

  const fetchServerTemplates = () => {
    const t = localStorage.getItem('mv_token');
    if (!t) return;
    fetch(`${API}/api/letter-templates`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setServerTemplates(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  const saveTemplate = (forceOverwrite = false) => {
    const name = saveName.trim();
    if (!name) return;
    const rawHtml = initializedRef.current ? getRawHtml() : '';
    const authToken = localStorage.getItem('mv_token');

    // Check for duplicate name in local + server templates
    if (!forceOverwrite) {
      const localMatch = savedTemplates.find(t => t.id !== '__draft__' && t.name.trim().toLowerCase() === name.toLowerCase());
      const serverMatch = serverTemplates.find(t => t.name.trim().toLowerCase() === name.toLowerCase());
      if (localMatch || serverMatch) {
        setSaveConflict(localMatch || serverMatch);
        return;
      }
    }

    setSaveConflict(null);

    // Overwrite existing
    if (forceOverwrite && saveConflict) {
      const existing = saveConflict;
      // Update local template if it exists in savedTemplates
      const localIdx = savedTemplates.findIndex(t => t.id !== '__draft__' && t.name.trim().toLowerCase() === name.toLowerCase());
      if (localIdx !== -1) {
        const updated = [...savedTemplates];
        updated[localIdx] = { ...updated[localIdx], form, html: rawHtml, ts: Date.now() };
        setSavedTemplates(updated);
        localStorage.setItem('cm_letter_tpls', JSON.stringify(updated));
      }
      // Update server template if it has a numeric id
      if (authToken && existing.id && typeof existing.id === 'number') {
        fetch(`${API}/api/letter-templates/${existing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ name, form_json: JSON.stringify(form), html_snapshot: rawHtml }),
        }).then(() => fetchServerTemplates()).catch(() => {});
      }
      setSaveName('');
      setSaveModalOpen(false);
      setSaveMsg('✓ Saved!');
      setTimeout(() => setSaveMsg(''), 2500);
      return;
    }

    // New template
    const tpl = { id: randStr(10), name, form, html: rawHtml, ts: Date.now() };
    const draft = savedTemplates.find(t => t.id === '__draft__');
    const rest = savedTemplates.filter(t => t.id !== '__draft__');
    const final = draft ? [draft, tpl, ...rest] : [tpl, ...rest];
    setSavedTemplates(final);
    localStorage.setItem('cm_letter_tpls', JSON.stringify(final));
    if (authToken) {
      fetch(`${API}/api/letter-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ name: tpl.name, form_json: JSON.stringify(tpl.form), html_snapshot: tpl.html }),
      }).then(() => fetchServerTemplates()).catch(() => {});
    }
    setSaveName('');
    setSaveModalOpen(false);
    setSaveMsg('✓ Saved!');
    setTimeout(() => setSaveMsg(''), 2500);
  };

  const loadTemplate = (t) => {
    const html = t.html || t.html_snapshot || '';
    const loadedForm = { ...EMPTY_FORM, ...(t.form || (t.form_json ? JSON.parse(t.form_json) : {})) };
    if (html) suppressWriteRef.current = true;
    setForm(loadedForm);
    setArmedHtml(null);
    if (html && iframeRef.current) {
      try {
        const doc = iframeRef.current.contentDocument;
        doc.open(); doc.write(html); doc.close();
        activateIframeEditing(doc);
        // Patch saved templates: inject spacing reset so old browser-inserted <div>
        // blocks don't produce gaps, and normalize legacy line-height values.
        const patch = doc.createElement('style');
        patch.textContent = 'div{margin:0;padding:0;}[contenteditable] div{line-height:inherit;}';
        doc.head?.appendChild(patch);
        doc.querySelectorAll('[style]').forEach(el => {
          el.style.lineHeight = el.style.lineHeight.replace('1.75', '1.5').replace('1.65', '1.4');
        });
        initializedRef.current = true;
        // Patch icon immediately — saved HTML may have old inline SVGs
        const iconEl = doc.querySelector('[data-part="file-icon-cell"]') ||
                       doc.querySelector('[data-part="file-icon-img"]')?.closest('td');
        if (iconEl) {
          const iconType = loadedForm.fileIconType || 'word';
          const src = (loadedForm.fileIconUrl && /^https?:\/\//.test(loadedForm.fileIconUrl))
            ? loadedForm.fileIconUrl
            : _ICON_CDN + iconType + '.png';
          iconEl.setAttribute('data-part', 'file-icon-cell');
          iconEl.setAttribute('data-icon-type', iconType);
          iconEl.style.cssText = 'vertical-align:middle;padding:0 16px 0 0;width:44px;line-height:0;';
          iconEl.innerHTML = `<img src="${src}" alt="${iconType}" data-part="file-icon-img" width="40" height="52" style="width:40px;height:52px;display:block;border-radius:7px;" />`;
        }
      } catch (e) { suppressWriteRef.current = false; }
    }
  };

  const deleteTemplate = (id) => {
    const updated = savedTemplates.filter(t => t.id !== id);
    setSavedTemplates(updated);
    localStorage.setItem('cm_letter_tpls', JSON.stringify(updated));
  };

  const deleteServerTemplate = (id) => {
    const authToken = localStorage.getItem('mv_token');
    if (!authToken) return;
    fetch(`${API}/api/letter-templates/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authToken}` },
    }).then(() => {
      fetchServerTemplates();
      setSaveMsg('✓ Deleted');
      setTimeout(() => setSaveMsg(''), 2500);
    }).catch(() => {});
  };

  const togglePublishServerTemplate = (id, currentStatus) => {
    const authToken = localStorage.getItem('mv_token');
    if (!authToken) return;
    const action = currentStatus === 'published' ? 'unpublish' : 'publish';
    fetch(`${API}/api/letter-templates/${id}/${action}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    }).then(() => {
      fetchServerTemplates();
      setSaveMsg(action === 'publish' ? '✓ Published!' : '✓ Unpublished');
      setTimeout(() => setSaveMsg(''), 2500);
    }).catch(() => {});
  };

  // ── useEffects: iframe re-init & surgical patches ─────────────────────────

  useEffect(() => {
    setArmedHtml(null);
    if (!form.template) { initializedRef.current = false; return; }
    if (suppressWriteRef.current) { suppressWriteRef.current = false; return; }
    writeIframe(form);
  }, [form.template]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    const el = doc?.querySelector('[data-part="logo"]');
    if (!el) return;
    const tplName = EMAIL_TEMPLATES.find(t => t.value === form.template)?.label || 'Notifications';
    const displayName = form.senderName || tplName;
    if (form.logoUrl) {
      el.innerHTML = `<img src="${form.logoUrl}" alt="${displayName}" style="height:32px;max-width:160px;display:block;" />`;
    } else if (form.template === 'plain') {
      el.textContent = displayName;
    } else {
      const nameEl = el.querySelector('span[style*="font-size:19px"]');
      if (nameEl) nameEl.textContent = displayName;
    }
  }, [form.logoUrl, form.senderName]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current) return;
    const ctaEl = iframeRef.current.contentDocument?.querySelector('[data-part="cta"]');
    if (ctaEl) {
      ctaEl.style.background = form.ctaColor;
      ctaEl.style.backgroundColor = form.ctaColor;
    }
  }, [form.ctaColor]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current) return;
    const ctaEl = iframeRef.current.contentDocument?.querySelector('[data-part="cta"]');
    if (ctaEl) {
      const abs = toAbsUrl(form.ctaUrl);
      ctaEl.setAttribute('href', abs);
    }
  }, [form.ctaUrl]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current || form.template !== 'outlook') return;
    const doc = iframeRef.current.contentDocument;
    const av = doc?.querySelector('[data-part="msg-avatar"]');
    if (!av) return;
    const sp = av.querySelector('span');
    if (sp) sp.textContent = getInitials(form.fromName || 'John Doe');
    av.style.backgroundColor = form.ctaColor;
  }, [form.fromName, form.ctaColor]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current || form.template !== 'outlook') return;
    const doc = iframeRef.current.contentDocument;
    const fromEl = doc?.querySelector('[data-field="msg-from"]');
    if (fromEl) fromEl.textContent = form.fromName || 'John Doe';
  }, [form.fromName]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current || form.template !== 'outlook') return;
    const doc = iframeRef.current.contentDocument;
    const emailEl = doc?.querySelector('[data-field="msg-email"]');
    if (emailEl) emailEl.textContent = '<' + (form.fromEmail || 'john.doe@contoso.com') + '>';
  }, [form.fromEmail]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current || form.template !== 'plain') return;
    const doc = iframeRef.current.contentDocument;
    const linkEl = doc?.querySelector('[data-part="plain-link"]');
    if (!linkEl) return;
    const abs = toAbsUrl(form.plainLinkUrl);
    if (linkEl.tagName === 'A') {
      linkEl.setAttribute('href', abs);
      if (form.plainLinkText) linkEl.textContent = form.plainLinkText;
    }
  }, [form.plainLinkUrl, form.plainLinkText]); // eslint-disable-line

  useEffect(() => {
    if (!initializedRef.current || !iframeRef.current) return;
    if (!['sharepoint', 'onedrive'].includes(form.template)) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    const iconEl = doc.querySelector('[data-part="file-icon-cell"]') ||
                   doc.querySelector('[data-part="file-icon-img"]')?.closest('td');
    if (!iconEl) return;
    const iconType = form.fileIconType || 'word';
    const src = (form.fileIconUrl && /^https?:\/\//.test(form.fileIconUrl))
      ? form.fileIconUrl
      : _ICON_CDN + iconType + '.png';
    iconEl.setAttribute('data-part', 'file-icon-cell');
    iconEl.setAttribute('data-icon-type', iconType);
    iconEl.style.cssText = 'vertical-align:middle;padding:0 16px 0 0;width:44px;line-height:0;';
    iconEl.innerHTML = `<img src="${src}" alt="${iconType}" data-part="file-icon-img" width="40" height="52" style="width:40px;height:52px;display:block;border-radius:7px;" />`;
  }, [form.fileIconType, form.fileIconUrl]); // eslint-disable-line

  useEffect(() => {
    if (tab !== 'import' || !importedHtml || !iframeRef.current) return;
    try {
      const doc = iframeRef.current.contentDocument;
      doc.open(); doc.write(importedHtml); doc.close();
      initializedRef.current = true;
    } catch (e) {}
  }, [tab, importedHtml]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'editor' && form.template) writeIframe(form);
  }, [tab]); // eslint-disable-line

  // ── Event handlers ─────────────────────────────────────────────────────────

  const selectTemplate = (tpl) => {
    setArmedHtml(null);
    setForm(f => ({
      ...EMPTY_FORM,
      logoUrl: f.logoUrl,
      senderName: tpl.label,          // always reset — prevents cross-template name bleed
      subject: tpl.subject,
      ctaColor: tpl.accent,
      unsubscribeUrl: f.unsubscribeUrl,
      template: tpl.value,
    }));
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('mv_token');
      const res = await fetch(`${API}/api/upload/icon`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setField('logoUrl', data.url);
    } catch {
      const reader = new FileReader();
      reader.onload = (ev) => setField('logoUrl', ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleFileIconUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('mv_token');
      const res = await fetch(`${API}/api/upload/icon`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setField('fileIconUrl', data.url);
    } catch {
      const reader = new FileReader();
      reader.onload = (ev) => setField('fileIconUrl', ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImportedHtml(ev.target.result); setArmedHtml(null); };
    reader.readAsText(file);
  };

  const generateLetter = async () => {
    const raw = tab === 'import' && importedHtml
      ? importedHtml
      : (initializedRef.current ? getRawHtml() : '');
    if (!raw) return;
    const html = await obfuscateForExport(raw);
    setArmedHtml(html);
    setArmMsg('Armed');
    setTimeout(() => setArmMsg(''), 6000);
  };

  const exportLetter = () => {
    if (!armedHtml) return;
    const blob = new Blob([armedHtml], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `letter-${Date.now()}.html`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const copyHtml = async () => {
    const raw = tab === 'import' && importedHtml
      ? importedHtml
      : (initializedRef.current ? getRawHtml() : '');
    if (!raw) return;
    try {
      await navigator.clipboard.writeText(await obfuscateForExport(raw));
      setCopyDone(true); setTimeout(() => setCopyDone(false), 2000);
    } catch {}
  };

  // ── Toolbar style helpers ──────────────────────────────────────────────────

  const tbtn = (extra = {}) => ({
    width: 26, height: 26, fontSize: 12, fontWeight: 700,
    borderRadius: 3, border: '1px solid var(--border)',
    cursor: 'pointer', background: 'var(--surface2)',
    color: 'var(--text)', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    ...extra,
  });

  const tselect = {
    fontSize: 11, height: 26, borderRadius: 3,
    border: '1px solid var(--border)',
    background: 'var(--surface2)', color: 'var(--text)',
    padding: '0 3px', cursor: 'pointer', flexShrink: 0,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg, #f5f5f5)' }}>

      {/* ── Top bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 16px', height: 52,
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', flexShrink: 0, zIndex: 10,
      }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(backPath)} style={{ fontSize: 13 }}>
          ← Back
        </button>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginRight: 'auto' }}>
          Letter Builder
        </span>

        <div style={{ display: 'flex', gap: 2, background: 'var(--surface2)', borderRadius: 6, padding: 3 }}>
          {['editor', 'import'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '4px 12px', fontSize: 12, fontWeight: 500, borderRadius: 4,
              border: 'none', cursor: 'pointer',
              background: tab === t ? 'var(--accent)' : 'transparent',
              color: tab === t ? '#fff' : 'var(--text2)',
            }}>{t === 'editor' ? 'Editor' : 'Import HTML'}</button>
          ))}
        </div>

        <button className="btn btn-ghost btn-sm" onClick={copyHtml} style={{ fontSize: 12 }}>
          {copyDone ? '✓ Copied!' : 'Copy HTML'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setSaveModalOpen(true)} style={{ fontSize: 12 }}>
          💾 Save
        </button>
        {saveMsg && <span style={{ fontSize: 11, color: '#107c10', fontWeight: 600, transition: 'opacity 0.3s' }}>{saveMsg}</span>}
        <button className="btn btn-primary btn-sm" onClick={generateLetter} style={{ fontSize: 12 }}>
          ⚡ Generate
        </button>
        <button
          className="btn btn-sm"
          onClick={exportLetter}
          disabled={!armedHtml}
          style={{
            fontSize: 12,
            background: armedHtml ? '#107c10' : 'var(--surface2)',
            color: armedHtml ? '#fff' : 'var(--text3)',
            border: armedHtml ? 'none' : '1px solid var(--border)',
            borderRadius: 6, padding: '5px 12px', cursor: armedHtml ? 'pointer' : 'not-allowed',
          }}
        >
          ↓ Export
        </button>
      </div>

      {/* ── Armed banner ── */}
      {armMsg && (
        <div style={{
          padding: '9px 20px', background: 'rgba(16,124,16,0.08)',
          borderBottom: '1px solid rgba(16,124,16,0.22)',
          fontSize: 12, color: '#107c10', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>🛡️</span>
          <strong>Armed:</strong>
          &nbsp;CSS classes randomized &bull; zero-width spaces injected &bull; URL entities encoded &bull; honeypot embedded &bull; 1–2 kb unique junk appended. Tags preserved.
          &nbsp;Click <strong>Export</strong> to download.
        </div>
      )}

      {/* ── Split pane ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* ── Left sidebar ── */}
        <div style={{
          borderRight: '1px solid var(--border)',
          overflowY: 'auto', overflowX: 'hidden',
          padding: '14px 14px 40px',
          background: 'var(--surface)',
        }}>
          {tab === 'import' ? (
            <>
              <div style={{ ...sHdr, marginTop: 0 }}>Import HTML</div>
              <div className="form-group">
                <label className="form-label">Load from file</label>
                <input type="file" accept=".html,.htm" className="form-input"
                  onChange={handleImportFile} style={{ fontSize: 12, padding: '6px 10px' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Or paste HTML</label>
                <textarea className="form-input" value={importedHtml}
                  onChange={e => { setImportedHtml(e.target.value); setArmedHtml(null); }}
                  rows={14} style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                  placeholder="Paste your email HTML here…" />
              </div>
              <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
                Click <strong>Generate</strong> to arm with obfuscation, URL encoding, honeypot, and unique junk block. Then <strong>Export</strong> to download.
              </p>
            </>
          ) : (
            <>
              {/* Template picker — collapsible dropdown */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ ...sHdr, marginTop: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 2 }}
                  onClick={() => setTplOpen(o => !o)}>
                  <span>Letter Template</span>
                  <span style={{ fontSize: 9, color: 'var(--text3)' }}>{tplOpen ? '▲' : '▼'}</span>
                </div>

                {/* Selected template pill — shown when collapsed */}
                {!tplOpen && (
                  <button onClick={() => setTplOpen(true)} style={{
                    width: '100%', padding: '7px 10px', borderRadius: 6, textAlign: 'left',
                    border: form.template ? `2px solid ${EMAIL_TEMPLATES.find(t => t.value === form.template)?.accent || 'var(--border)'}` : '2px dashed var(--border)',
                    background: form.template ? `${EMAIL_TEMPLATES.find(t => t.value === form.template)?.accent || '#888'}18` : 'var(--surface2)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    {form.template
                      ? <div style={{ width: 7, height: 7, borderRadius: '50%', background: EMAIL_TEMPLATES.find(t => t.value === form.template)?.accent || '#888', flexShrink: 0 }} />
                      : <span style={{ fontSize: 12, color: 'var(--text3)' }}>▼</span>}
                    <span style={{ fontSize: 12, fontWeight: form.template ? 600 : 400, color: form.template ? 'var(--text)' : 'var(--text3)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {form.template
                        ? EMAIL_TEMPLATES.find(t => t.value === form.template)?.label
                          || savedTemplates.find(t => t.id === form.template)?.name
                          || serverTemplates.find(t => String(t.id) === String(form.template))?.name
                          || form.template
                        : 'Select Letter Template…'}
                    </span>
                    {form.template && <span style={{ fontSize: 9, color: 'var(--text3)', flexShrink: 0 }}>Change ▼</span>}
                  </button>
                )}

                {/* Dropdown panel */}
                {tplOpen && (
                  <div style={{ background: 'var(--surface2)', borderRadius: 6, border: '1px solid var(--border)', overflow: 'hidden' }}>
                    {/* Built-in */}
                    <div style={{ padding: '4px 8px 2px', fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.06)' }}>Built-in</div>
                    {EMAIL_TEMPLATES.map(tpl => {
                      const sel = form.template === tpl.value;
                      return (
                        <button key={tpl.value} onClick={() => { selectTemplate(tpl); setTplOpen(false); }} style={{
                          width: '100%', padding: '7px 10px', textAlign: 'left',
                          border: 'none', borderBottom: '1px solid var(--border)',
                          background: sel ? `${tpl.accent}18` : 'transparent',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: tpl.accent, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: sel ? 700 : 500, color: sel ? tpl.accent : 'var(--text)' }}>{tpl.label}</div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.subject || 'Custom message'}</div>
                          </div>
                          {sel && <span style={{ fontSize: 11, color: tpl.accent, flexShrink: 0 }}>✓</span>}
                        </button>
                      );
                    })}

                    {/* Local drafts */}
                    {savedTemplates.length > 0 && (
                      <>
                        <div style={{ padding: '4px 8px 2px', fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.06)' }}>My Drafts</div>
                        {savedTemplates.map(t => (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderBottom: '1px solid var(--border)', background: 'transparent' }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: t.id === '__draft__' ? 'var(--text3)' : 'var(--text)', fontStyle: t.id === '__draft__' ? 'italic' : 'normal' }}>
                              {t.id === '__draft__' ? '⏱ ' : ''}{t.name}
                            </span>
                            <button onClick={() => { loadTemplate(t); setTplOpen(false); }} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', color: 'var(--text2)', flexShrink: 0 }}>Load</button>
                            <button onClick={() => deleteTemplate(t.id)} style={{ fontSize: 11, width: 18, height: 18, borderRadius: 3, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--text3)', flexShrink: 0, lineHeight: 1, padding: 0 }}>✕</button>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Server templates */}
                    {serverTemplates.length > 0 && (
                      <>
                        <div style={{ padding: '4px 8px 2px', fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.06)' }}>
                          {user?.role === 'superadmin' ? 'All Templates' : 'My Templates'}
                        </div>
                        {serverTemplates.map(t => (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderBottom: '1px solid var(--border)', background: 'transparent' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{t.name}</div>
                              {t.status === 'published'
                                ? <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                                    <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(16,124,16,0.12)', color: '#107c10', fontWeight: 600 }}>PUB</span>
                                    <span style={{ fontSize: 9, color: 'var(--text3)' }}>by {t.owner_username}</span>
                                  </div>
                                : user?.role === 'superadmin' && <div style={{ fontSize: 9, color: 'var(--text3)' }}>by {t.owner_username}</div>}
                            </div>
                            <button onClick={() => { loadTemplate(t); setTplOpen(false); }} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', color: 'var(--text2)', flexShrink: 0 }}>Load</button>
                            {user?.role === 'superadmin' && (
                              <button onClick={() => togglePublishServerTemplate(t.id, t.status)} title={t.status === 'published' ? 'Unpublish' : 'Publish to all admins'}
                                style={{ fontSize: 10, padding: '2px 5px', borderRadius: 3, border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0, background: t.status === 'published' ? 'rgba(16,124,16,0.1)' : 'var(--surface)', color: t.status === 'published' ? '#107c10' : 'var(--text2)' }}>
                                {t.status === 'published' ? '✓' : '↑'}
                              </button>
                            )}
                            <button onClick={() => deleteServerTemplate(t.id)} style={{ fontSize: 11, width: 18, height: 18, borderRadius: 3, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--text3)', flexShrink: 0, lineHeight: 1, padding: 0 }}>✕</button>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Message */}
              <div style={sHdr}>Message</div>
              <div className="form-group">
                <label className="form-label">Subject line</label>
                <input className="form-input" value={form.subject}
                  onChange={e => setField('subject', e.target.value)} placeholder="Email subject" />
              </div>
              <div className="form-group">
                <label className="form-label">Preheader</label>
                <input className="form-input" value={form.preheader}
                  onChange={e => setField('preheader', e.target.value)} placeholder="Inbox preview text" />
              </div>

              {/* Sender / Header */}
              <div style={sHdr}>{form.template === 'plain' ? 'Sender' : 'Header'}</div>
              <div className="form-group">
                <label className="form-label">{form.template === 'plain' ? 'Sender / Org name' : 'Header label'}</label>
                <input className="form-input" value={form.senderName}
                  onChange={e => setField('senderName', e.target.value)}
                  placeholder={form.template ? EMAIL_TEMPLATES.find(t => t.value === form.template)?.label || 'Brand name' : 'Brand name'} />
              </div>
              <div className="form-group">
                <label className="form-label">Logo URL</label>
                <input className="form-input" value={form.logoUrl}
                  onChange={e => setField('logoUrl', e.target.value)} placeholder="https://example.com/logo.png" />
              </div>
              <div className="form-group">
                <label className="form-label">Upload logo</label>
                <input type="file" accept="image/*" className="form-input"
                  onChange={handleLogoFile} style={{ fontSize: 12, padding: '6px 10px' }} />
              </div>

              {/* File icon — only for SharePoint / OneDrive */}
              {['sharepoint', 'onedrive'].includes(form.template) && (
                <>
                  <div style={sHdr}>File Icon</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {FILE_ICON_PRESETS.map(p => (
                      <button key={p.type} onClick={() => { setField('fileIconUrl', ''); setField('fileIconType', p.type); }} style={{
                        padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                        border: `2px solid ${form.fileIconType === p.type && !form.fileIconUrl ? p.color : 'var(--border)'}`,
                        background: form.fileIconType === p.type && !form.fileIconUrl ? `${p.color}18` : 'var(--surface2)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                      }}>
                        <img src={_svgToDataUri(FILE_ICON_SVGS[p.type])} alt={p.label} style={{ width: 26, height: 34, display: 'block' }} />
                        <span style={{ fontSize: 9, color: 'var(--text2)', fontWeight: 500 }}>{p.label}</span>
                      </button>
                    ))}
                    {/* Uploaded custom icon shown as a picker option */}
                    {form.fileIconUrl && (
                      <button onClick={() => {}} style={{
                        padding: '6px 10px', borderRadius: 6, cursor: 'default',
                        border: '2px solid var(--primary, #0078d4)',
                        background: 'rgba(0,120,212,.08)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                      }}>
                        <img src={form.fileIconUrl} alt="Custom" style={{ width: 26, height: 34, objectFit: 'contain', display: 'block' }}
                          onError={e => { e.currentTarget.style.display = 'none'; }} />
                        <span style={{ fontSize: 9, color: 'var(--primary, #0078d4)', fontWeight: 600 }}>Custom</span>
                      </button>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Custom icon URL</label>
                    <input className="form-input" value={form.fileIconUrl}
                      onChange={e => setField('fileIconUrl', e.target.value)} placeholder="https://…" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Upload icon</label>
                    <input type="file" accept="image/*" className="form-input"
                      onChange={handleFileIconUpload} style={{ fontSize: 12, padding: '6px 10px' }} />
                  </div>
                </>
              )}

              {/* Outlook — From person (name + email in body, drives avatar) */}
              {form.template === 'outlook' && (
                <>
                  <div style={sHdr}>From (Message Sender)</div>
                  <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, lineHeight: 1.55 }}>
                    The person the message appears to come from. Changes the avatar initials and the <em>John Doe</em> line in the body.
                  </p>
                  <div className="form-group">
                    <label className="form-label">From name</label>
                    <input className="form-input" value={form.fromName}
                      onChange={e => setField('fromName', e.target.value)} placeholder="John Doe" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">From email address</label>
                    <input className="form-input" value={form.fromEmail}
                      onChange={e => setField('fromEmail', e.target.value)} placeholder="john.doe@contoso.com" />
                  </div>
                </>
              )}

              {/* CTA — hidden for plain text */}
              {form.template !== 'plain' && (
                <>
                  <div style={sHdr}>CTA Button</div>
                  <div className="form-group">
                    <label className="form-label">Button URL</label>
                    <input className="form-input" value={form.ctaUrl}
                      onChange={e => setField('ctaUrl', e.target.value)} placeholder="https://" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Button color</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={form.ctaColor}
                        onChange={e => setField('ctaColor', e.target.value)}
                        style={{ width: 34, height: 28, padding: 2, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', background: 'none' }} />
                      <input className="form-input" value={form.ctaColor}
                        onChange={e => setField('ctaColor', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unsubscribe URL</label>
                    <input className="form-input" value={form.unsubscribeUrl}
                      onChange={e => setField('unsubscribeUrl', e.target.value)} placeholder="https://" />
                  </div>
                </>
              )}

              {/* Plain text link — only for plain template */}
              {form.template === 'plain' && (
                <>
                  <div style={sHdr}>Protected Link</div>
                  <div style={{ padding: '8px 10px', background: 'rgba(0,120,212,0.06)', border: '1px solid rgba(0,120,212,0.2)', borderRadius: 5, fontSize: 11, color: '#0078d4', marginBottom: 12, lineHeight: 1.5 }}>
                    🔒 This URL is rendered as an invisible field in the HTML — protected with entity encoding, ZWSP injection, and anti-scan scripts on export. Appears as a natural text link to the reader.
                  </div>
                  <div className="form-group">
                    <label className="form-label">Link URL</label>
                    <input className="form-input" value={form.plainLinkUrl}
                      onChange={e => setField('plainLinkUrl', e.target.value)} placeholder="domain.com/page" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Link display text</label>
                    <input className="form-input" value={form.plainLinkText}
                      onChange={e => setField('plainLinkText', e.target.value)} placeholder="Click here to continue" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Link color</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={form.ctaColor}
                        onChange={e => setField('ctaColor', e.target.value)}
                        style={{ width: 34, height: 28, padding: 2, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', background: 'none' }} />
                      <input className="form-input" value={form.ctaColor}
                        onChange={e => setField('ctaColor', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unsubscribe URL</label>
                    <input className="form-input" value={form.unsubscribeUrl}
                      onChange={e => setField('unsubscribeUrl', e.target.value)} placeholder="https://" />
                  </div>
                </>
              )}

              {/* Live links table */}
              <div style={sHdr}>My Live Links</div>
              {liveLinks.length === 0 ? (
                <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                  No links found. Generate links from the dashboard and they'll appear here for quick copy.
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px', color: 'var(--text3)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Link Name</th>
                      <th style={{ width: 52, padding: '4px 4px', color: 'var(--text3)', fontWeight: 600, fontSize: 10 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveLinks.map(link => {
                      const id = link.id;
                      const fullUrl = link.spintax || link.best_url || link.gate_url || link.go_url || link.redirect_page_url || link.page_url || link.external_url || link.url;
                      const label = link.label || link.name || link.title || String(link.id);
                      return (
                        <tr key={id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 6px', color: 'var(--text2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fullUrl}>
                            {label}
                          </td>
                          <td style={{ padding: '4px 4px', textAlign: 'right' }}>
                            <button onClick={() => copyLink(fullUrl, id)} style={{
                              fontSize: 10, padding: '3px 8px', borderRadius: 3,
                              border: '1px solid var(--border)', cursor: 'pointer',
                              background: copiedLinkId === id ? '#107c10' : 'var(--surface)',
                              color: copiedLinkId === id ? '#fff' : 'var(--text2)',
                            }}>
                              {copiedLinkId === id ? '✓ Copied' : 'Copy'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {form.template && (
                <div style={{ marginTop: 18, padding: '9px 11px', background: 'var(--surface2)', borderRadius: 6, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.55 }}>
                    Click any text in the preview to edit. Use <strong>B I U</strong> toolbar for formatting. Insert tags with the chips. When done, click <strong>⚡ Generate</strong> then <strong>↓ Export</strong>.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Right pane ── */}
        <div style={{ background: '#e4e4e4', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', minWidth: 0 }}>

          {/* Status bar */}
          <div style={{
            padding: '6px 14px', background: 'var(--surface)',
            borderBottom: '1px solid var(--border)',
            fontSize: 11, color: armedHtml ? '#107c10' : 'var(--text3)', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {armedHtml ? <><span>🛡️</span> Armed &amp; ready to export</> : 'Live Preview — click any text to edit'}
          </div>

          {/* Formatting toolbar */}
          {tab === 'editor' && form.template && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
              padding: '5px 10px', background: 'var(--surface)',
              borderBottom: '1px solid var(--border)', flexShrink: 0,
            }}>
              {/* Text formatting */}
              {[
                { cmd: 'bold',          label: 'B', style: { fontWeight: 900 } },
                { cmd: 'italic',        label: 'I', style: { fontStyle: 'italic' } },
                { cmd: 'underline',     label: 'U', style: { textDecoration: 'underline' } },
                { cmd: 'strikeThrough', label: 'S', style: { textDecoration: 'line-through' } },
              ].map(({ cmd, label, style }) => (
                <button key={cmd}
                  onMouseDown={e => { e.preventDefault(); execCmd(cmd); }}
                  style={tbtn(style)}
                  title={cmd}
                >{label}</button>
              ))}

              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px', flexShrink: 0 }} />

              {/* Font family */}
              <select
                onMouseDown={saveIframeSelection}
                onChange={e => { restoreIframeSelection(); execCmd('fontName', e.target.value); e.target.value = ''; }}
                style={tselect}
              >
                <option value="">Font</option>
                {['Arial', 'Segoe UI', 'Georgia', 'Courier New', 'Verdana', 'Times New Roman'].map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>

              {/* Font size */}
              <select
                onMouseDown={saveIframeSelection}
                onChange={e => { applyFontSize(Number(e.target.value)); e.target.value = ''; }}
                style={tselect}
              >
                <option value="">Size</option>
                {[10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32].map(s => (
                  <option key={s} value={s}>{s}px</option>
                ))}
              </select>

              {/* Text color */}
              <input
                type="color"
                title="Text color"
                defaultValue="#333333"
                onMouseDown={saveIframeSelection}
                onChange={e => { restoreIframeSelection(); execCmd('styleWithCSS', true); execCmd('foreColor', e.target.value); }}
                style={{ width: 26, height: 26, padding: 2, border: '1px solid var(--border)', borderRadius: 3, cursor: 'pointer', background: 'none', flexShrink: 0 }}
              />

              {/* Remove formatting */}
              <button
                onMouseDown={e => { e.preventDefault(); execCmd('removeFormat'); }}
                style={tbtn({ width: 'auto', padding: '0 7px', fontSize: 10, fontWeight: 400 })}
                title="Remove formatting"
              >Clear</button>

              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px', flexShrink: 0 }} />

              {/* Tag format selector + chips */}
              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px', flexShrink: 0 }} />
              <select
                value={tagFormat}
                onChange={e => setTagFormat(e.target.value)}
                style={{ ...tselect, fontSize: 10 }}
                title="Tag format"
              >
                {TAG_FORMATS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              {TAG_NAMES.map(name => {
                const tag = tagFormat === '[]' ? `[${name}]` : tagFormat === '%%' ? `%${name}%` : `{{${name}}}`;
                return (
                  <button
                    key={name}
                    onMouseDown={e => { e.preventDefault(); execCmd('insertText', tag); }}
                    style={{
                      padding: '1px 5px', fontSize: 10, borderRadius: 8, flexShrink: 0,
                      border: '1px solid rgba(0,120,212,0.35)', cursor: 'pointer',
                      background: 'rgba(0,120,212,.07)', color: '#0078d4', whiteSpace: 'nowrap',
                    }}
                    title={`Insert ${tag}`}
                  >{tag}</button>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!form.template && tab === 'editor' && !importedHtml && (
            <div style={{
              position: 'absolute', inset: 0, top: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 12, color: 'var(--text3)',
              pointerEvents: 'none',
            }}>
              <div style={{ fontSize: 44, opacity: 0.2 }}>✉️</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Select a template to start</div>
              <div style={{ fontSize: 12 }}>Your email will appear here</div>
            </div>
          )}

          <iframe
            ref={iframeRef}
            title="Email Preview"
            sandbox="allow-same-origin allow-scripts"
            style={{ flex: 1, border: 'none', width: '100%' }}
          />
        </div>
      </div>

      {/* ── Save modal ── */}
      {saveModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 10, padding: 24,
            width: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: 'var(--text)' }}>Save Template</div>
            <input
              className="form-input"
              value={saveName}
              onChange={e => { setSaveName(e.target.value); setSaveConflict(null); }}
              onKeyDown={e => e.key === 'Enter' && saveTemplate()}
              placeholder="Template name…"
              autoFocus
            />
            {saveConflict && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(218,54,51,.08)', border: '1px solid rgba(218,54,51,.3)', borderRadius: 6, fontSize: 12, color: '#b91c1c' }}>
                A template named <strong>"{saveName.trim()}"</strong> already exists. Overwrite it?
              </div>
            )}
            <div style={{ height: 12 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setSaveModalOpen(false); setSaveConflict(null); }}>Cancel</button>
              {saveConflict
                ? <button className="btn btn-danger btn-sm" onClick={() => saveTemplate(true)}>Overwrite</button>
                : <button className="btn btn-primary btn-sm" onClick={() => saveTemplate()} disabled={!saveName.trim()}>Save</button>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
